/* eslint-disable require-jsdoc */
import {
  AUDIO_MUTE_BIT,
  AUDIO_STAT_BIT,
  AUX_STAT_BIT,
  LOGGER_DOMAIN,
  LOGGER_DOMAIN_OVERSEA,
  loggerProxy,
  NAME,
  NETWORK_TYPE,
  SMALL_STAT_BIT,
  VIDEO_MUTE_BIT,
  VIDEO_STAT_BIT,
  DATA_CHANNEL_FROM_TYPE_BIT
} from '../common/constants';
import { IRemoteTrack, IRoom, ITrack } from '../enums';
import RtcError from '../common/rtc-error';
import ErrorCode from '../common/error-code';
import { encode } from './pb';
import { encodeSSOPackage } from './sso/sso-encoder';
import loggerManager from './log/logger-manager';

export const getEnv = function () {
  const params = new URLSearchParams(location.search);
  return params.get('trtc_env') || '';
};

// 默认信令域名后缀
const DEFAULT_SIGNAL_DOMAIN_SUFFIX = '.rtc.qq.com';

/**
 * 获取测试信令域名
 * - 如果 env 包含 '.'，认为是完整域名，直接返回
 * - 如果 env 不包含 '.'，认为是环境前缀，拼接默认后缀
 */
export const getTestSignalDomain = function (env: string) {
  if (env.includes('.')) {
    return env;
  }
  return `${env}${DEFAULT_SIGNAL_DOMAIN_SUFFIX}`;
};

// sdkappid 小于 14亿则为海外的 appid
export const isOverseaSdkAppId = (sdkAppId: string | number) => Number(sdkAppId) < 1400000000;

export const getLoggerUrl = function (sdkAppId: string | number, cmdtype: string) {
  let domain;
  if (loggerProxy) {
    domain = loggerProxy;
  } else {
    domain = isOverseaSdkAppId(sdkAppId)
      ? LOGGER_DOMAIN_OVERSEA
      : LOGGER_DOMAIN;
  }
  const random = Math.floor(Math.random() * 2 ** 31);

  return `${domain}/v5/AVQualityReportSvc/C2S?random=${random}&sdkappid=${sdkAppId}&cmdtype=${cmdtype}`;
};

let networkTypeFromWebRTC: keyof typeof NETWORK_TYPE = 'unknown';
// 0: '未知'; 1: "wifi"; 2: "3G"; 3: "2G"; 4: "4G"; 5: "有线"
export function getNetworkType(): keyof typeof NETWORK_TYPE {
  listenNetTypeChanged();
  // @ts-ignore
  const { userAgent, connection } = navigator;

  // 'wifi','4g','3g','3gnet','2g'
  let userAgentNetType = (userAgent.match(/NetType\/\S+/) || [])[0] || '';

  userAgentNetType = userAgentNetType.toLowerCase().replace('nettype/', '');
  if (userAgentNetType === '3gnet') {
    userAgentNetType = '3g';
  }

  // 'bluetooth': 蓝牙
  // 'cellular': 蜂窝网络(e.g., EDGE, HSPA, LTE, etc.)
  // 'ethernet': 以太网
  // 'none': 无连接
  // 'mixed': 多类型混合
  // 'other': 类型可知，但不可枚举
  // 'unknown': 有连接，但类型未知
  // 'wifi': Wi-Fi
  // 'wimax': WiMAX
  const connectionType = connection && connection.type && connection.type.toLowerCase();

  // '2g','3g','4g','slow-2'
  let connectionEffectiveType =
    connection &&
    connection.effectiveType &&
    connection.effectiveType.toLowerCase();
  if (connectionEffectiveType === 'slow-2') {
    connectionEffectiveType = '2g';
  }

  return connectionType ? convertNetworkType(connectionType, connectionEffectiveType) : networkTypeFromWebRTC;
}

function onNetTypeChanged() {
  loggerManager.warn('netType changed', getNetworkType());
}
let isNetTypeChangedListenerAdded = false;

function listenNetTypeChanged() {
  if (isNetTypeChangedListenerAdded) return;
  isNetTypeChangedListenerAdded = true;
  // @ts-ignore
  navigator.connection?.addEventListener('typechange', onNetTypeChanged);
}

function convertNetworkType(networkType: string, connectionEffectiveType?: keyof typeof NETWORK_TYPE): keyof typeof NETWORK_TYPE {
  // @ts-ignore
  if (NETWORK_TYPE[networkType]) return networkType;
  switch (networkType) {
    case 'cellular':
    case 'wimax':
      return connectionEffectiveType || 'unknown';
    case 'ethernet':
      return 'wired';
    case 'none':
    case 'other':
    default:
      return 'unknown';
  }
}

export function setNetworkTypeFromWebRTC(type: string) {
  networkTypeFromWebRTC = convertNetworkType(type);
}

export function getNumNetworkType() {
  return NETWORK_TYPE[getNetworkType()];
}

/**
 *
 * @param target
 * @param source
 */
export function copyProperties(target: any, source: any) {
  for (const key of Reflect.ownKeys(source)) {
    if (key !== 'constructor' && key !== 'prototype' && key !== 'name') {
      const desc = Object.getOwnPropertyDescriptor(source, key) || '';
      Object.defineProperty(target, key, desc);
    }
  }
  return target;
}

export function bytes2ms(bytes: number, sampleRate = 48000) {
  return samples2ms(bytes / 4, sampleRate);
}
export function samples2ms(samples: number, sampleRate = 48000) {
  return (samples * 1000) / sampleRate;
}
export function ms2bytes(ms: number, sampleRate = 48000) {
  return ms2samples(ms, sampleRate) * 4;
}
export function ms2samples(ms: number, sampleRate = 48000) {
  return (ms * sampleRate) / 1000;
}
// @ts-ignore
export const glog = typeof window !== 'undefined' && typeof window.glog === 'function' ? window.glog : () => { };

/*
 * 当前浏览器是否是中文语言
 * @export
 * @return {Boolean}
 */
export const isLangChinese = () => {
  let lang = navigator.language;
  lang = lang.substring(0, 2);
  return lang === 'zh';
};

export const isPlainObject = function (value: any): value is object {
  // 判断类型不是 Object 的
  if (
    !value ||
    typeof value !== 'object' ||
    Object.prototype.toString.call(value) != '[object Object]'
  ) {
    return false;
  }
  // 返回指定对象的原型 判断对象有没有原型，没有原型的对象算纯粹对象
  const proto = Object.getPrototypeOf(value);
  if (proto === null) {
    // Object.create(null) 的情况
    return true;
  }
  // 最后判断是不是通过 "{}" 或 "new Object" 方式创建的对象
  // 如果 proto 有 constructor属性，Ctor 的值就为 proto.constructor，
  // 原型的 constructor 属性指向关联的构造函数
  const Ctor = Object.prototype.hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return (
    typeof Ctor === 'function' &&
    // 用于检测构造函数Ctor的 prototype 属性是否出现在某个实例对象(Ctor)的原型链上。
    Ctor instanceof Ctor &&
    // 如果 Ctor 类型是  "function" ，并且调用Function.prototype.toString 方法后得到的字符串 与 "function Object() { [native code] }" 这样的字符串相等就返回true
    // 用来区分 自定义构造函数和 Object 构造函数
    Function.prototype.toString.call(Ctor) === Function.prototype.toString.call(Object)
  );
};
/**
 * 使用尾递归优化，计算斐波那契数
 * @export
 */
export function fibonacci(n: number, ac1 = 1, ac2 = 1): number {
  if (n <= 1) {
    return ac2;
  }

  return fibonacci(n - 1, ac2, ac1 + ac2);
}

/**
 * 获取重连超时间隔
 * 根据斐波那契数来计算，最大超时间隔为 30s，最小间隔为 2s
 */
export function getReconnectionTimeout(reconnectionCount: number): number {
  // 最大间隔 30s
  if (reconnectionCount > 8) {
    return 30 * 1000;
  }
  return fibonacci(reconnectionCount) * 1000;
}

/**
 * 获取value的类型
 */
export function getValueType(value: any) {
  return Reflect.apply(Object.prototype.toString, value, [])
    .replace(/^\[object\s(\w+)\]$/, '$1')
    .toLowerCase();
}

export const isFunction = (param: any): param is (...args: any[]) => unknown => typeof param === 'function';
export const isUndefined = (param: any): param is undefined => typeof param === 'undefined';
export const isString = (param: any): param is string => typeof param === 'string';
export const isNumber = (param: any): param is number => typeof param === 'number';
export const isBoolean = (param: any): param is boolean => typeof param === 'boolean';
export const isObject = (param: any): param is Record<string, any> => getValueType(param) === 'object';
export const isArray = (param: any): param is any[] => getValueType(param) === 'array';
export const isMediaStreamTrack = (track: any): track is MediaStreamTrack => getValueType(track) === 'MediaStreamTrack'.toLowerCase();
export const isRemoteTrack = (track: ITrack): track is IRemoteTrack => track.isRemote;
export const isPromise = (param: any): param is Promise<any> => getValueType(param) === 'promise';
export const isConstructor = (param: any) => isFunction(param) && param.prototype.constructor === param;
export const getConstructorName = (param: any) => (isConstructor(param) ? param.prototype.constructor.name : '');

export const isAudioWorkletSupported = typeof AudioWorkletNode !== 'undefined';

export const isSetSinkIdSupported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

/**
 * promise.any 的简单实现
 * 单元测试用例参考：test/unit/utils.test.js
 * @export
 * @param {Promise[]} promiseList
 * @return {Promise}
 */
export function promiseAny<T>(promiseList: Promise<T>[]) {
  return new Promise<T>((resolve, reject) => {
    const errorList: Error[] = [];
    promiseList.forEach(promise => {
      promise.then(resolve)
        .catch(error => {
          errorList.push(error);
          if (errorList.length === promiseList.length) {
            reject(errorList);
          }
        });
    });
  });
}

/*
 * 获取向下取整的 performance.now() 值
 * @export
 * @return {Number}
 */
export function performanceNow() {
  // 在不支持 performance.now 的浏览器中，使用 Date.now()
  // 例如 ie 9，ie 10，避免加载 sdk 时报错
  if (!performance || !performance.now) {
    return Date.now();
  }
  return Math.floor(performance.now());
}

const fillZero = (num: any) => (+num < 10 ? `0${num}` : num);

export const getInternalVersion = (version: any) => {
  const matchVersion = version.match(/^\d+\.\d+\.\d+/)[0];
  if (!matchVersion) {
    return version;
  }
  const arr = matchVersion.split('.');
  const innerVersion = fillZero(arr[1]) + fillZero(arr[2]);
  if (arr[1] - 15 > 0) {
    arr[1] = '15';
  }
  if (arr[2] - 15 > 0) {
    arr[2] = '15';
  }
  const result = `${arr.join('.')}.${innerVersion}`;
  return result;
};

/**
 * 从 innerVersion 还原出真实的版本号
 * innerVersion 格式: major.clampedMinor.clampedPatch.MMPP
 * 其中 MMPP 的前两位是真实 minor，后两位是真实 patch
 * 例如: '5.15.15.1600' => '5.16.0'
 */
export const getVersionFromInternalVersion = (innerVersion: string): string => {
  const parts = innerVersion.split('.');
  if (parts.length !== 4) {
    return innerVersion;
  }
  const major = parts[0];
  const tail = parts[3];
  if (tail.length < 4) {
    return innerVersion;
  }
  const minor = parseInt(tail.substring(0, tail.length - 2), 10);
  const patch = parseInt(tail.substring(tail.length - 2), 10);
  return `${major}.${minor}.${patch}`;
};

/**
 * Has own property.
 *
 * @type { Function }
 */

const has = Object.prototype.hasOwnProperty;

/**
 * To string.
 *
 * @type {Function}
 */

const { toString } = Object.prototype;

/**
 * Test whether a value is "empty".
 *
 * @param {Mixed} val
 * @return {Boolean}
 */
export function isEmpty(val: any) {
  // Null and Undefined...
  if (val == null) return true;

  // Booleans...
  if ('boolean' === typeof val) return false;

  // Numbers...
  if ('number' === typeof val) return val === 0;

  // Strings...
  if ('string' === typeof val) return val.length === 0;

  // Functions...
  if ('function' === typeof val) return val.length === 0;

  // Arrays...
  if (Array.isArray(val)) return val.length === 0;

  // Errors...
  if (val instanceof Error) return val.message === '';

  // Objects...
  if (isPlainObject(val)) {
    switch (Object.prototype.toString.call(val)) {
      // Maps, Sets, Files and Errors...
      case '[object File]':
      case '[object Map]':
      case '[object Set]': {
        return (val as Set<unknown>).size === 0;
      }

      // Plain objects...
      case '[object Object]': {
        for (const key in val) {
          if (has.call(val, key)) return false;
        }

        return true;
      }
    }
  }

  // Anything else...
  return false;
}

export function getMuteStateFromFlag(flag: number, userId: string): MuteState {
  return {
    userId,
    hasAudio: !!(flag & AUDIO_STAT_BIT),
    hasVideo: !!(flag & VIDEO_STAT_BIT),
    hasAuxiliary: !!(flag & AUX_STAT_BIT),
    hasSmall: !!(flag & SMALL_STAT_BIT),
    audioMuted: !!(flag & AUDIO_MUTE_BIT),
    videoMuted: !!(flag & VIDEO_MUTE_BIT),
    audioAvailable: !!(flag & AUDIO_STAT_BIT) && !(flag & AUDIO_MUTE_BIT), // hasAudio && !audioMuted
    videoAvailable: !!(flag & VIDEO_STAT_BIT) && !(flag & VIDEO_MUTE_BIT),  // hasVideo && !videoMuted
    hasDatachannel: !!(flag & DATA_CHANNEL_FROM_TYPE_BIT)
  };
}

/**
 * 将用户传入的参数转换成符合 webrtc 格式的 turn server
 * @export
 * @param {Object} options
 * @return {Object}
 */
export function getTurnServer(options: TurnServerOptions) {
  const turnServer: RTCIceServer = {
    urls: (options.url.startsWith('turn:') || options.url.startsWith('turns:')) ? options.url : `turn:${options.url}`
  };
  if (!isUndefined(options.username) && !isUndefined(options.credential)) {
    turnServer.username = options.username;
    turnServer.credential = options.credential;
    // @ts-ignore
    turnServer.credentialType = 'password';
    if (!isUndefined(options.credentialType)) {
      // @ts-ignore
      turnServer.credentialType = options.credentialType;
    }
  }
  return turnServer;
}


/**
 * ipv4 地址转 uint32
 * @export
 * @param {string} ipStr
 * @param {boolean} [isBigEndian=true] 是否大端序，否则小端序。
 * @return {Number}
 */
export function ipv4ToUint32(ipStr: string, isBigEndian = true) {
  if (!isString(ipStr)) {
    return 0;
  }
  const arr = ipStr.split('.');
  if (isBigEndian) {
    // 大端序 最后转为无符号 uint32
    return (
      ((Number(arr[0]) << 24) | (Number(arr[1]) << 16) | (Number(arr[2]) << 8) | Number(arr[3])) >>>
      0
    );
  }
  // 小端序 最后转为无符号 uint32
  return (
    ((Number(arr[3]) << 24) | (Number(arr[2]) << 16) | (Number(arr[1]) << 8) | Number(arr[0])) >>>
    0
  );
}

interface IndexedObject {
  [key: string]: any;
}

// -----------------深度合并工具函数-----------------
/**
 * 深度 merge 两个对象，遇到子项为数组时会覆盖原值。不支持参数传入数组。merge source to target。
 * @param {IndexedObject} target 目标对象
 * @param {IndexedObject} source 来源对象
 * @param {String[]} [keysIgnore] 要忽略的 keys。命中的 key 会在merge时被忽略
 * @param {*[]} [valuesIgnore] 要忽略的 values。命中的 value 会在merge时被忽略
 * @returns {Number} merge的次数（只有key相同value不同的时候才会merge），如果target和source既不是数组也不是object，则返回0
 */
export const deepMerge = function (target: IndexedObject, source: IndexedObject, keysIgnore?: string[], valuesIgnore?: any[]) {
  // 非 Object 类型则直接 return
  if (!(isObject(target) && isObject(source))) {
    return 0;
  }

  let mergedCount = 0;
  const keys = Object.keys(source);
  let tmpKey;
  for (let i = 0, len = keys.length; i < len; i++) {
    tmpKey = keys[i];
    if (isUndefined(source[tmpKey]) || (keysIgnore && keysIgnore.includes(tmpKey))) {
      continue;
    }
    if (isObject(target[tmpKey]) && isObject(source[tmpKey])) {
      // 递归merge
      mergedCount += deepMerge(target[tmpKey], source[tmpKey], keysIgnore, valuesIgnore);
    } else {
      if (valuesIgnore && valuesIgnore.includes(source[tmpKey])) {
        continue;
      }
      if (target[tmpKey] !== source[tmpKey]) {
        target[tmpKey] = deepClone(source[tmpKey]);
        mergedCount += 1;
      }
    }
  }
  return mergedCount;
};

/**
 * 深拷贝，支持 object, array
 */
export function deepClone<T>(obj: T): T {
  if (isArray(obj)) {
    const copy = [] as unknown as T;
    (obj as unknown as Array<unknown>).forEach((element, index) => {
      (copy as unknown as Array<unknown>)[index] = deepClone(element);
    });
    return copy;
  }

  if (isObject(obj)) {
    const copy = {} as T;
    Object.keys(obj as object).forEach(key => {
      (copy as { [key: string]: any; })[key] = deepClone((obj as { [key: string]: any; })[key]);
    });
    return copy;
  }

  return obj;
}

export const getViewListFromView = (view: HTMLElement | HTMLElement[] | string | null) => {
  let viewList: HTMLElement[] = [];
  if (isArray(view)) {
    viewList = [...view];
  } else if (isString(view)) {
    const element = document.getElementById(view);
    if (element) {
      viewList.push(element);
    }
  } else if (view) {
    viewList.push(view);
  }
  return viewList;
};

export const getContainerFromElement = (element: HTMLElement | string | null) => {
  if (isString(element)) {
    return document.getElementById(element);
  }
  return element;
};

const formater = (time: Date) => {
  const pad = (num: number) => (num < 10 ? `0${num}` : `${num}`);
  const year = time.getFullYear();
  const month = time.getMonth() + 1;
  const day = time.getDate();
  const hours = pad(time.getHours());
  const minutes = pad(time.getMinutes());
  const seconds = pad(time.getSeconds());

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};
export const formatedTime = () => formater(new Date());

/**
 * 封装 JSON.stringify 并过滤 keys 输出
 * @param {Object} params
 * @param {string[]} keysToInclude
 * @returns
 */
export function stringify(params: any, { keysToInclude, keysToExclude }: { keysToInclude?: string[], keysToExclude?: string[]; }): string {
  try {
    if (isArray(params)) {
      return `[${params.map(item => stringify(item, { keysToInclude, keysToExclude })).join(',')}]`;
    }
    if (!isPlainObject(params) || (!isArray(keysToInclude) && !isArray(keysToExclude))) return JSON.stringify(params);
    const newObject: {
      [propName: string]: any;
    } = {};
    // 空间换时间
    const keysSet = new Set(keysToInclude);
    const keysToExcludeSet = new Set(keysToExclude);
    Object.keys(params).forEach(key => {
      if ((keysToExcludeSet.size === 0 && keysSet.has(key)) || (keysSet.size === 0 && !keysToExcludeSet.has(key))) {
        // @ts-ignore
        newObject[key] = isPlainObject(params[key]) || isArray(params[key]) ? JSON.parse(stringify(params[key], { keysToExclude, keysToInclude })) : params[key];
      }
    });
    return JSON.stringify(newObject);
  } catch (error) {
    return '{}';
  }
}

export function stringifyIncludeValue(params: any, valueToInclude = false): string {
  const keysToInclude: string[] = [];
  Object.keys(params).forEach((key: string) => {
    if (valueToInclude === params[key]) {
      keysToInclude.push(key);
    }
  });
  return stringify(params, { keysToInclude });
}

export function getStringByteLength(str: string) {
  // 中文占两个字节，英文占一个字节
  return str.replace(/[\u4e00-\u9fa5]/g, 'aa').length;
}

export const isPortrait = () => {
  if (window.screen?.orientation) {
    return !!window.screen?.orientation?.type?.includes('portrait');
  }
  return window.orientation === 0 || window.orientation === 180;
};

/**
 * 加载图片
 */
export const loadImage = async (paramsImage: string | HTMLImageElement) => new Promise<HTMLImageElement>((resolve, reject) => {
  let image: HTMLImageElement;
  if (isString(paramsImage)) {
    image = new Image();
    image.crossOrigin = 'anonymous'; // 开启跨域访问，避免出现加载非同源的图片资源时，开启水印生成的 videoTrack 是黑屏的问题。
    image.src = paramsImage;
  } else {
    image = paramsImage;
    if (image.complete) {
      resolve(image);
      return;
    }
  }
  image.onload = () => resolve(image);
  image.onerror = () => {
    reject(new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: `load image failed, url: ${paramsImage}`
    }));
  };
});
export const getUint32Version = (version: string) => {
  const versionArray = version.split('.');
  return (+versionArray[0] << 24) | (+versionArray[1] << 16) | (+versionArray[2] << 8) | (+versionArray[3] << 0);
};

export const convertObjectNumberToInt = (obj: any) => {
  Object.keys(obj).forEach(key => {
    if (isNumber(obj[key]) && (key.startsWith('uint') || key.startsWith('int'))) {
      obj[key] = Math.floor(obj[key]);
    } else if (isPlainObject(obj[key]) || isArray(obj[key])) {
      convertObjectNumberToInt(obj[key]);
    }
  });
  return obj;
};

export function delay(time: number, onTimeoutId?: (timeoutId: number) => void) {
  return new Promise(resolve => {
    const id = setTimeout(resolve, time);
    if (onTimeoutId) onTimeoutId(id);
  });
}
/**
 * 给 Promise 函数节流
 * 多次调用异步函数，若第一次 promise 还没结束，那么第二次调用时直接返回第一次的 promise.
 * @param {Function} promiseFn 返回 promise 的异步函数
 * @returns
 */
export function throttlePromise(promiseFn: (...args: any) => Promise<any>, context?: any) {
  let promise: Promise<any> | null = null;
  return function (this: any, ...args: any) {
    if (promise) {
      return promise;
    }
    promise = promiseFn.apply(context || this, args);
    promise.finally(() => promise = null);
    return promise;
  };
}

export function normalizeUrl(url: string) {
  return url.replace(/(^|[^:])\/{2,}/g, '$1/');
}

export function getMediaStreamTrackInfo(track: MediaStreamTrack) {
  try {
    const { width, height, frameRate, sampleRate, sampleSize, channelCount } = track.getSettings?.();
    const settingString = track.kind === NAME.AUDIO ? `${sampleRate}x${sampleSize}@${channelCount}` : `${width}x${height}@${frameRate}`;
    // @ts-ignore
    const statsString = track.stats ? ` stats: ${JSON.stringify(track.stats).replaceAll('"', '')}` : '';
    return `${track.id} ${track.readyState} muted:${track.muted} ${track.kind} ${track.label} ${settingString}${statsString}`;
  } catch (error) {
    return '';
  }
}

/**
 * 计算缩放比例
 * @param {VideoProfile} source 源分辨率
 * @param {VideoProfile} target 目标分辨率
 * @returns {number} 源分辨率 / 目标分辨率
 */
export function calculateScaleResolutionDownNumber(source: { width: number, height: number; }, target: { width: number, height: number; }) {
  // 分辨率一样 return 1
  if (source.width * source.height === target.width * target.height) return 1;
  // 移动端竖屏模式下，需要横屏分辨率，但会采集出竖屏分辨率。此时计算 scaleResolutionDownBy 需要特殊处理。例如：
  // 1. 设置采集 640 * 480 (target)
  // 2. 采集卡只能采集 1080p，最终采集出的分辨率是 1080 * 1920 (source)
  // 3. 此时计算 scale 不能用 1080 / 640，而应该用 1080 / 480, 1920 / 640
  if (isPortrait() && target.width > target.height && source.height > target.width) {
    return Math.max(source.width / target.height, source.height / target.width, 1);
  }
  return Math.max(source.width / target.width, source.height / target.height, 1);
}

export function isRotate90Or270(rotation?: 0 | 90 | 180 | 270) {
  return rotation === 90 || rotation === 270;
}

export async function loadVideo(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.play().then(() => resolve(video));
    video.onerror = () => {
      reject(video.error);
      // reject(new RtcError({
      //   code: ErrorCode.INVALID_PARAMETER,
      //   message: `load video failed, url: ${url}`
      // }));
    };
  });
}

/* 深拷贝，仅拷贝基本类型，数组、普通对象，当传入 proxy 时返回的是普通对象 */
export function deepCloneBasic(value: any, cache = new WeakMap()) {
  // 处理基本数据类型
  if (typeof value !== 'object' || value === null) {
    return value;
  }
  // 处理循环引用
  if (cache.has(value)) {
    return cache.get(value);
  }
  // 处理数组
  if (Array.isArray(value)) {
    const result: any[] = [];
    cache.set(value, result);
    value.forEach((item, index) => {
      result[index] = deepCloneBasic(item, cache);
    });
    return result;
  }
  // 处理普通对象
  if (Object.prototype.toString.call(value) === '[object Object]') {
    const result: Record<string | symbol, any> = {};
    cache.set(value, result);
    // 复制所有自有属性（包括可枚举的 Symbol 属性）
    Reflect.ownKeys(value).forEach(key => {
      result[key] = deepCloneBasic(value[key], cache);
    });
    return result;
  }

  // 其他类型（如 Date、RegExp 等）直接返回
  return value;
}

export enum REPORT_TYPE {
  END_REPORT = 2001,
  LOG = 2002,
  KEY_METRIC_REPORT = 2003
}

interface PB_REPORT_JSON {
  data: object;
  random: number;
  sdkAppId: number;
  gzip?: number;
}

function buildProtocolBufferJSON(data: object, type: REPORT_TYPE, sdkAppId: number, gzip?: boolean) {
  // http + sso 上报, SSO 服务器不会透传 URL 参数, 需要把 URL 参数放到 json 中
  let dataWithURLParams: PB_REPORT_JSON = {
    data,
    random: Math.floor(Math.random() * 2 ** 31),
    sdkAppId
  };
  if (!isUndefined(gzip)) {
    dataWithURLParams = { ...dataWithURLParams, gzip: +gzip };
  }
  // 转 pb 的 encode 函数要求 JSON 的 key 顺序和 protobuf 定义的顺序一致
  return {
    // native 使用, web sdk 不关心
    uint32_sdkappid: 0,
    uint64_from_uin: 0,
    uint32_timestamp: 0,
    uint32_seq: 0,
    msg_common_info: {
      msg_device_info: {
        enum_device_type: 0,
        str_device_brand: '',
        str_device_model: '',
        str_device_board: '',
        str_device_cpu_abi: ''
      },
      msg_system_info: {
        enum_os_type: 0,
        str_os_version: '',
        msg_network_info: 0
      },
      msg_network_info: {
        enum_network_type: 0
      }
    },
    // web sdk 实际使用
    msg_report_content: {
      uint32_type: type,
      bytes_report_data: JSON.stringify(dataWithURLParams)
    }
  };
}

export function buildSSOPackage(data: object, type: REPORT_TYPE, sdkAppId: number, gzip?: boolean) {
  try {
    // 1. data -> json
    const pbJson = buildProtocolBufferJSON(data, type, sdkAppId, gzip);
    // 2. json -> pb
    const bytesReportData = encode(pbJson);
    // 3. pb -> sso
    return encodeSSOPackage(bytesReportData, sdkAppId);
  } catch (error) {
    return JSON.stringify(data);
  }
}
export function concatArrayBuffers(buffer1: ArrayBuffer, buffer2: ArrayBuffer) {
  const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
  tmp.set(new Uint8Array(buffer1), 0);
  tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
  return tmp.buffer;
}
export function getLast16Bits(number: number) {
  return (number & 0xffff) >>> 0;
}

export function getFirst16Bits(number: number) {
  return (number & 0xffff0000) >>> 0;
}

export function isVideoMixerOutputTrack(track?: CanvasCaptureMediaStreamTrack|MediaStreamTrack|null, room?: IRoom) :boolean {
  loggerManager.debug('isVideoMixerOutputTrack', track, room, track === room?.videoMixerOutputTrack);
  if (!track || !room?.isUsingVideoMixerInternalTrack) return false;
  // if (track instanceof CanvasCaptureMediaStreamTrack && track.canvas.id.includes('trtc_mix')) return true;
  if (room.videoMixerOutputTrack && track === room.videoMixerOutputTrack) return true;
  return false;
}

/**
 * Detect whether an image source is animated (GIF/APNG) based on its URL.
 * Supports regular URLs (by file extension), Data URLs (by MIME type), and Blob URLs (defaults to static).
 * @param src - The image source URL string
 * @returns An object with `animated` flag and optional `mimeType`
 */
export function detectImageType(src: string): { animated: boolean; mimeType?: string } {
  if (!src) return { animated: false };

  // Data URL: extract MIME type from the data URI scheme
  if (src.startsWith('data:')) {
    const mimeMatch = src.match(/^data:(image\/[^;,]+)/);
    if (mimeMatch) {
      const mime = mimeMatch[1].toLowerCase();
      const isAnimated = mime === 'image/gif' || mime === 'image/apng';
      return { animated: isAnimated, mimeType: mime };
    }
    return { animated: false };
  }

  // Blob URL: cannot detect type from URL alone, default to static
  if (src.startsWith('blob:')) return { animated: false };

  // Regular URL: check file extension
  try {
    const url = new URL(src, 'https://placeholder.invalid');
    const ext = url.pathname.split('.').pop()
      ?.toLowerCase();
    if (ext === 'gif') return { animated: true, mimeType: 'image/gif' };
    if (ext === 'apng') return { animated: true, mimeType: 'image/apng' };
  } catch { /* ignore invalid URLs */ }

  return { animated: false };
}
